import {
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { v2InvestorBrowseStartups } from '@workspace/api-backend'
import {
  v2InvestorAddWatchdogCriterionMutation,
  v2InvestorCreateFounderInviteMutation,
  v2InvestorDeleteMcpDataMutation,
  v2InvestorDisconnectMcpConnectionMutation,
  v2InvestorDismissFocusOfferMutation,
  v2InvestorEditFollowMutation,
  v2InvestorFocusFromSearchMutation,
  v2InvestorFollowStartupMutation,
  v2InvestorGetFocusOptions,
  v2InvestorGetFocusQueryKey,
  v2InvestorGetMcpSettingsOptions,
  v2InvestorGetMcpSettingsQueryKey,
  v2InvestorGetStartupRelationshipOptions,
  v2InvestorGetWatchdogOptions,
  v2InvestorGetWatchdogQueryKey,
  v2InvestorListFollowsOptions,
  v2InvestorListFollowsQueryKey,
  v2InvestorListFounderInvitesOptions,
  v2InvestorListFounderInvitesQueryKey,
  v2InvestorListMcpConnectionsOptions,
  v2InvestorListMcpConnectionsQueryKey,
  v2InvestorListNotesOptions,
  v2InvestorListNotesQueryKey,
  v2InvestorListNotificationsOptions,
  v2InvestorListNotificationsQueryKey,
  v2InvestorMarkNotificationsReadMutation,
  v2InvestorMarkWatchdogStartupSeenMutation,
  v2InvestorNotifyWhenInChatgptMutation,
  v2InvestorPassMutation,
  v2InvestorRecentSearchesOptions,
  v2InvestorRecentSearchesQueryKey,
  v2InvestorRemoveFounderInviteMutation,
  v2InvestorRemoveWatchdogCriterionMutation,
  v2InvestorReopenPassMutation,
  v2InvestorRestoreSearchOptions,
  v2InvestorSaveFocusMutation,
  v2InvestorSavePrivateNoteMutation,
  v2InvestorSearchMutation,
  v2InvestorSearchSuggestionsOptions,
  v2InvestorSearchSuggestionsQueryKey,
  v2InvestorSetWatchdogCriterionLockedMutation,
  v2InvestorSetWatchdogPausedMutation,
  v2InvestorShareStartupMutation,
  v2InvestorUnfollowStartupMutation,
  v2InvestorUpdateMcpSettingsMutation,
} from '@workspace/api-backend/react-query'
import { useEffect } from 'react'
import { useInvestorContext } from '../../../contexts/investor-guard'
import { useMyMembershipsQuery } from '../../../hooks/api'

/** The assistant is open per investor, so the settings are read for the investor the page is about. */
/** Anything that changes a follow, a pass or a note makes what a startup is to the investor stale. */
function invalidateRelationship(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ['investor-browse'] })
  return queryClient.invalidateQueries({
    predicate: (query) =>
      (query.queryKey[0] as { _id?: string } | undefined)?._id ===
      'v2InvestorGetStartupRelationship',
  })
}

/** What one startup is to this investor: the follow, what was published since, the saved read, the pass. */
/** The API's answer for a startup that is missing or that the person may not open: one body for both. */
export const isResourceNotFound = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { error?: { message?: string } }).error?.message === 'Resource not found'

export function useStartupRelationship(startupId: string | null) {
  return useQuery({
    ...v2InvestorGetStartupRelationshipOptions({ path: { startupId: startupId ?? '' } }),
    enabled: startupId !== null,
    select: (response) => response.data,
  })
}

/**
 * Whether the person has the assistant through any of their investors, for pages outside an
 * investor's own app (a shared profile): there is no investor context to read the settings from.
 */
export function useAssistantAccess(enabled: boolean): boolean {
  const { data: memberships } = useMyMembershipsQuery({ enabled })
  const investors = (memberships ?? []).filter((m) => m.type === 'investor')
  const results = useQueries({
    queries: investors.map((m) => ({
      ...v2InvestorGetMcpSettingsOptions({ query: { investorId: m.id } }),
      enabled,
    })),
  })
  return results.some((result) => result.data?.data.assistantEnabled === true)
}

/** Search what founders have shared: a phrase in, quotes and states out. Each search is its own answer, so none is cached. */
/**
 * `onSaved` runs for every saved search, even after the page that asked has been left (callbacks given to `mutate`
 * do not), so a search answered while the investor is elsewhere is still the one they come back to.
 */
export function useSearchStartupsMutation(callbacks?: {
  onSaved?: (searchId: string, previousId: string | undefined) => void
}) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorSearchMutation(),
    onSuccess: async (response, variables) => {
      if (response.data.searchId)
        callbacks?.onSaved?.(response.data.searchId, variables.body?.previousId)
      await queryClient.invalidateQueries({ queryKey: v2InvestorRecentSearchesQueryKey() })
    },
  })
}

/** How often a pending search (the firm check still running) is run again, so its quotes show when the check completes. */
const PENDING_POLL_MS = 5_000

/**
 * A saved search (`?s=`), run again with the criteria it was saved with. Restoring never saves or counts. While the
 * answer is pending, it is run again every few seconds until the check completes.
 */
export function useRestoreSearchQuery(searchId: string | undefined) {
  return useQuery({
    ...v2InvestorRestoreSearchOptions({ path: { searchId: searchId ?? '' } }),
    enabled: !!searchId,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.data?.kind === 'pending' ? PENDING_POLL_MS : false,
  })
}

/** Starting points from the investor's focus, with their live counts. */
export function useSearchSuggestionsQuery() {
  return useQuery({
    ...v2InvestorSearchSuggestionsOptions(),
  })
}

/** The watchdog: what it looks for, whether it is paused, and the startups it has found. */
export function useWatchdogQuery(enabled = true) {
  return useQuery({
    ...v2InvestorGetWatchdogOptions(),
    select: (response) => response.data,
    enabled,
  })
}

/** How many watchdog matches the investor has not seen yet (the "N new" on the tab and the Search badge). */
export function useNewWatchdogMatches(enabled = true): number {
  const { data } = useWatchdogQuery(enabled)
  return data?.matches.filter((match) => match.seenAt === null).length ?? 0
}

/**
 * The watchdog's focus and Settings' focus are one list, so a change to either refetches both and Search's starters at
 * once, also while their page is not open (`refetchType: 'all'`): the cache is fresh before the investor gets there, so
 * no page shows the focus as it was, not even for a moment, and no editor opens on old values.
 */
function invalidateWatchdog(queryClient: QueryClient) {
  return Promise.all(
    [
      v2InvestorGetWatchdogQueryKey(),
      v2InvestorGetFocusQueryKey(),
      v2InvestorSearchSuggestionsQueryKey(),
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey, refetchType: 'all' }))
  )
}

/** The investor's own focus, as Settings shows it: the same focus the watchdog shows. */
export function usePersonalFocusQuery() {
  return useQuery({ ...v2InvestorGetFocusOptions(), select: (response) => response.data })
}

export function useSavePersonalFocusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorSaveFocusMutation(),
    onSuccess: () => invalidateWatchdog(queryClient),
  })
}

export function useSetWatchdogPausedMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorSetWatchdogPausedMutation(),
    onSuccess: () => invalidateWatchdog(queryClient),
  })
}

/** The investor visited a startup's data room: its watchdog match, if new, stops being new. */
export function useMarkWatchdogStartupSeenMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorMarkWatchdogStartupSeenMutation(),
    onSuccess: () => invalidateWatchdog(queryClient),
  })
}

/** Opening a startup (its page or its side panel) is seeing it: its watchdog match, if new, stops being new. */
export function useMarkWatchdogSeenOnOpen(startupId: string) {
  const { mutate } = useMarkWatchdogStartupSeenMutation()
  useEffect(() => {
    mutate({ path: { startupId } })
  }, [startupId, mutate])
}

export function useAddWatchdogCriterionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorAddWatchdogCriterionMutation(),
    onSuccess: () => invalidateWatchdog(queryClient),
  })
}

export function useRemoveWatchdogCriterionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorRemoveWatchdogCriterionMutation(),
    onSuccess: () => invalidateWatchdog(queryClient),
  })
}

export function useSetWatchdogCriterionLockedMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorSetWatchdogCriterionLockedMutation(),
    onSuccess: () => invalidateWatchdog(queryClient),
  })
}

/** "Use as my focus": the search's criteria become the investor's locked focus. */
export function useFocusFromSearchMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorFocusFromSearchMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2InvestorSearchSuggestionsQueryKey() }),
  })
}

/** "Not now": the focus offer is not asked again. */
export function useDismissFocusOfferMutation() {
  return useMutation({
    ...v2InvestorDismissFocusOfferMutation(),
  })
}

/** Pass on a startup privately: a reason and an optional "would look again when". Never a follow. */
export function usePassStartupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorPassMutation(),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: v2InvestorListNotesQueryKey() }),
        invalidateRelationship(queryClient),
      ]),
  })
}

/** Share startup: the drafted message and the link the investor holds. Nothing is recorded. */
export function useShareStartupMutation() {
  return useMutation({
    ...v2InvestorShareStartupMutation(),
  })
}

/** The investor's most recent searches, newest first, for the list under the search box. */
export function useRecentSearchesQuery() {
  return useQuery({
    ...v2InvestorRecentSearchesOptions(),
  })
}

export function useMcpSettingsQuery() {
  const { investor } = useInvestorContext()
  return useQuery({
    ...v2InvestorGetMcpSettingsOptions({ query: { investorId: investor.id } }),
    // Every page and the layout read this: a failed read must not refetch each time one of them
    // mounts (the error screen mounts the layout, which would start the loop again). The error
    // screen's button retries.
    retryOnMount: false,
  })
}

export function useUpdateMcpSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorUpdateMcpSettingsMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2InvestorGetMcpSettingsQueryKey() }),
  })
}

export function useNotificationsQuery(enabled = true) {
  return useQuery({ ...v2InvestorListNotificationsOptions(), refetchInterval: 60_000, enabled })
}

/** How many updates the investor has not read yet (the "N new" on Following's Updates tab and the Following badge). */
export function useUnreadUpdates(enabled = true): number {
  return useNotificationsQuery(enabled).data?.data.unread ?? 0
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorMarkNotificationsReadMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2InvestorListNotificationsQueryKey() }),
  })
}

export function useFollowsQuery() {
  return useQuery(v2InvestorListFollowsOptions())
}

export function useUnfollow() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorUnfollowStartupMutation(),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: v2InvestorListFollowsQueryKey() }),
        invalidateRelationship(queryClient),
      ]),
  })
}

/** The investor's own note on a startup; empty text removes it. */
export function useSavePrivateNote() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorSavePrivateNoteMutation(),
    onSuccess: () => invalidateRelationship(queryClient),
  })
}

/** Change what a follow waits for; everything else the follow holds stays. */
export function useEditFollow() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorEditFollowMutation(),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: v2InvestorListFollowsQueryKey() }),
        invalidateRelationship(queryClient),
      ]),
  })
}

export function useNotesQuery() {
  return useQuery(v2InvestorListNotesOptions())
}

export function useReopenPass() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorReopenPassMutation(),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: v2InvestorListNotesQueryKey() }),
        invalidateRelationship(queryClient),
      ]),
  })
}

export function useDeleteMcpData() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorDeleteMcpDataMutation(),
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

export function useFollowStartupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorFollowStartupMutation(),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: v2InvestorListFollowsQueryKey() }),
        invalidateRelationship(queryClient),
      ]),
  })
}

/** The assistants this person has signed in. Pass `pollMs` while waiting for a first sign-in. */
export function useConnectionsQuery(pollMs?: number) {
  return useQuery({
    ...v2InvestorListMcpConnectionsOptions(),
    refetchInterval: pollMs ?? false,
  })
}

export function useDisconnectAssistant() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorDisconnectMcpConnectionMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2InvestorListMcpConnectionsQueryKey() }),
  })
}

export function useNotifyWhenInChatgpt() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorNotifyWhenInChatgptMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2InvestorListMcpConnectionsQueryKey() }),
  })
}

export interface BrowseFilters {
  sort?: 'joined' | 'name'
  q?: string
  signedUp?: 'week'
  raising?: 'now'
  region?: 'europe'
  focus?: 'fits'
}

export const BROWSE_PAGE = 25

/** The startups the investor may open, newest first, a page at a time. */
export function useBrowseStartups(filters: BrowseFilters) {
  return useInfiniteQuery({
    queryKey: ['investor-browse', filters],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data } = await v2InvestorBrowseStartups({
        query: { sort: 'joined', ...filters, limit: BROWSE_PAGE, offset: pageParam },
        throwOnError: true,
      })
      return data.data
    },
    // An empty page ends the list even if the total says more, so scrolling never asks for the same offset forever.
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, page) => n + page.items.length, 0)
      return last.items.length > 0 && loaded < last.total ? loaded : undefined
    },
  })
}

/** The founder invites the investor has made, newest first. */
export function useFounderInvitesQuery() {
  return useQuery({
    ...v2InvestorListFounderInvitesOptions(),
    select: (response) => response.data.invites,
  })
}

/** Ask a founder to join. A duplicate answers with the invite that already exists and no link. */
export function useCreateFounderInviteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorCreateFounderInviteMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2InvestorListFounderInvitesQueryKey() }),
  })
}

/** Removes an invite still waiting for its founder; its link stops working. */
export function useRemoveFounderInviteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2InvestorRemoveFounderInviteMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2InvestorListFounderInvitesQueryKey() }),
  })
}
